import React from 'react';

/**
 * Blinking notification badge for unread messages
 * @param {number} count - Number of unread messages
 * @param {boolean} isRead - If true, shows as static dot (not blinking)
 * @param {string} size - 'sm', 'md', or 'lg'
 */
const UnreadBadge = ({ count = 0, isRead = false, size = 'sm' }) => {
  if (count === 0 && isRead) return null;
  
  const sizeClasses = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  const countSizeClasses = {
    sm: 'min-w-[18px] h-[18px] text-[10px]',
    md: 'min-w-[20px] h-[20px] text-xs',
    lg: 'min-w-[24px] h-[24px] text-sm'
  };

  // If there are unread messages, show count badge with blinking effect
  if (count > 0) {
    return (
      <span className={`
        inline-flex items-center justify-center
        ${countSizeClasses[size]}
        bg-red-500 text-white font-bold rounded-full
        animate-pulse shadow-lg shadow-red-500/50
      `}>
        {count > 99 ? '99+' : count}
      </span>
    );
  }

  // If no count but not marked as read, show blinking dot
  if (!isRead) {
    return (
      <span className={`
        inline-block ${sizeClasses[size]}
        bg-red-500 rounded-full
        animate-pulse shadow-lg shadow-red-500/50
      `} />
    );
  }

  // Static dot (read but showing indicator)
  return (
    <span className={`
      inline-block ${sizeClasses[size]}
      bg-slate-300 rounded-full
    `} />
  );
};

/**
 * Dot only indicator (no count)
 */
export const BlinkingDot = ({ active = false, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  if (!active) return null;

  return (
    <span className={`
      inline-block ${sizeClasses[size]}
      bg-red-500 rounded-full
      animate-pulse shadow-lg shadow-red-500/50
    `} />
  );
};

export default UnreadBadge;
